/**
 * writeQueue/sync.ts — background sync worker.
 *
 * Polls the IndexedDB queue every TICK_MS, picks `pending` items whose
 * nextRetryAt has passed, dispatches them via dispatch.ts, and updates
 * the record's status based on success / failure.
 *
 * Backoff schedule (per plan doc):
 *   attempts=0 → next try in 1s
 *   attempts=1 → next try in 2s
 *   attempts=2 → next try in 4s
 *   attempts=3 → next try in 8s
 *   attempts=4 → next try in 16s
 *   attempts=5 → next try in 30s
 *   attempts=6+ → next try in 60s (capped)
 *
 * After MAX_ATTEMPTS failed tries, the record flips to `failed_giveup`
 * and stays in the queue until the editor explicitly retries or discards.
 *
 * Worker fires immediately on:
 *   - Initial start() call
 *   - `window.online` event
 *   - `document.visibilitychange` when becoming visible
 *   - Explicit kick() call (for use after JWT refresh, etc.)
 *
 * Plan reference: docs/archive/admin-rebuild/admin-fragility-rebuild-plan.md § Item 1.
 *
 * Status: LIVE (2026-05-19). Started from AdminLayout.tsx's useEffect
 * with `start(() => getClient(env))`. Tear down via the returned
 * controller's `.stop()` (AdminLayout does this on env change / unmount).
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import {
  MutationRecord,
  listByStatus,
  listReadyToRetry,
  updateMutation,
  pruneSynced,
} from './db';
import { notifyChange } from './queue';
import { dispatch } from './dispatch';

const TICK_MS = 500;
const MAX_ATTEMPTS = 8;
const PRUNE_INTERVAL_MS = 60 * 60 * 1000; // prune synced rows older than 1h
const PRUNE_TICK_MS = 5 * 60 * 1000; // run prune check every 5 min

function backoffMs(attempts: number): number {
  if (attempts <= 0) return 1000;
  if (attempts === 1) return 2000;
  if (attempts === 2) return 4000;
  if (attempts === 3) return 8000;
  if (attempts === 4) return 16000;
  if (attempts === 5) return 30000;
  return 60000;
}

interface SyncWorker {
  stop(): void;
  kick(): void;
}

/**
 * Start the sync worker. Returns a controller with stop() and kick().
 * Safe to call multiple times — second+ calls are no-ops (the existing
 * worker is reused).
 *
 * The getClient callback returns the current Supabase client. It's a
 * callback (not a static reference) so the worker stays correct across
 * env switches and client recreations.
 */
let activeWorker: SyncWorker | null = null;

export function start(getClient: () => SupabaseClient | null): SyncWorker {
  if (activeWorker) return activeWorker;

  let stopped = false;
  let tickPending = false;

  // Process all ready-to-retry records.
  //
  // Head-of-line blocking fix (2026-05-19): processOne is fired without
  // await on the loop, so one record's 30s dispatch hang doesn't block
  // other records from being attempted. Each processOne marks its
  // record 'inflight' immediately, and listReadyToRetry filters on
  // status='pending', so subsequent ticks naturally skip records already
  // in flight without needing tick-level coordination. New clicks enqueue
  // a 'pending' record and the very next tick (≤500ms later) picks them
  // up regardless of how many older records are still hanging.
  async function processTick(): Promise<void> {
    if (stopped || tickPending) return;
    tickPending = true;
    try {
      const client = getClient();
      if (!client) return; // no client yet — try next tick

      const now = Date.now();
      const ready = await listReadyToRetry(now);
      if (ready.length === 0) return;

      // Fire each record's processing INDEPENDENTLY (no await on the for
      // loop). Each call self-manages its lifecycle: marks record inflight
      // → awaits dispatch with timeout → flips to synced/pending/
      // failed_giveup. Errors are caught at the record level; one record's
      // hang or rejection doesn't abort the others.
      for (const record of ready) {
        if (stopped) break;
        processOne(record, client).catch(err =>
          console.error('[writeQueue.sync] processOne threw:', err),
        );
      }
    } catch (err) {
      console.error('[writeQueue.sync] tick threw:', err);
    } finally {
      tickPending = false;
    }
  }

  async function processOne(record: MutationRecord, client: SupabaseClient): Promise<void> {
    // Mark inflight so UI surfaces "syncing..." for this row. If the
    // inflight write itself fails (IDB quota / connection lost), the
    // record stays in 'pending' on disk and the next tick will retry —
    // BETTER than leaving a zombie 'inflight' record that the tick
    // skips forever.
    record.status = 'inflight';
    try {
      await updateMutation(record);
    } catch (err) {
      console.warn(`[writeQueue.sync] failed to mark ${record.id} inflight — leaving as pending for next tick:`, (err as Error).message);
      record.status = 'pending'; // revert local mutation, the on-disk state is unchanged
      return;
    }
    notifyChange();

    let result;
    try {
      // 30s timeout matches the historical approve_draft timeout pattern.
      // The new server-tier path has its own 5s upstream cap, so server-
      // mediated dispatches typically resolve well before this fires.
      // The 30s is the outer-bound safety for the direct-Supabase fallback
      // path which can still suffer the legacy hang.
      result = await Promise.race([
        dispatch(record.op, record.payload, client),
        new Promise<{ error: string }>((_, reject) =>
          setTimeout(() => reject(new Error(`Dispatch timed out after 30s`)), 30000),
        ),
      ]);
    } catch (err) {
      result = { error: (err as Error).message };
    }

    if (!result.error) {
      // Success — mark synced. Pruned later by the prune tick.
      record.status = 'synced';
      record.lastError = null;
      try {
        await updateMutation(record);
      } catch (err) {
        // Couldn't write 'synced' to IDB. The mutation already landed at
        // Supabase; the only consequence is the record stays 'inflight' on
        // disk until prune-or-restart cleans it up. Log and move on.
        console.warn(`[writeQueue.sync] mutation ${record.id} succeeded on server but couldn't mark synced locally:`, (err as Error).message);
      }
      notifyChange();
      return;
    }

    // Failure — bump attempts, schedule next retry, OR give up.
    record.attempts += 1;
    record.lastError = result.error;
    if (record.attempts >= MAX_ATTEMPTS) {
      record.status = 'failed_giveup';
      record.nextRetryAt = Number.MAX_SAFE_INTEGER; // never auto-retry
      console.error(
        `[writeQueue.sync] mutation ${record.id} (op=${record.op}, target=${record.targetRowId}) gave up after ${record.attempts} attempts: ${result.error}`,
      );
    } else {
      record.status = 'pending';
      record.nextRetryAt = Date.now() + backoffMs(record.attempts);
      console.warn(
        `[writeQueue.sync] mutation ${record.id} (op=${record.op}) failed attempt ${record.attempts}: ${result.error}. Retrying in ${backoffMs(record.attempts)}ms.`,
      );
    }
    try {
      await updateMutation(record);
    } catch (err) {
      // IDB write of the post-failure state failed. The record stays in
      // 'inflight' on disk; next tick will skip it. To recover, the user
      // can refresh — the page-load path will see 'inflight' records and
      // can either resurrect them (not implemented yet) or treat them as
      // pending. For now, log loudly.
      console.error(`[writeQueue.sync] mutation ${record.id} failed AND couldn't update IDB to record the failure:`, (err as Error).message);
    }
    notifyChange();
  }

  // Startup recovery — flip any 'inflight' records from a previous session
  // back to 'pending'. These are zombies from a tab that closed mid-
  // dispatch; without this step they'd never be retried (listReadyToRetry
  // filters on 'pending'). Runs async; the first tick will pick them up.
  async function recoverInflightZombies(): Promise<void> {
    try {
      const zombies = await listByStatus('inflight');
      if (zombies.length === 0) return;
      console.log(`[writeQueue.sync] recovered ${zombies.length} inflight zombie(s) from prior session — flipping to pending`);
      for (const z of zombies) {
        z.status = 'pending';
        z.nextRetryAt = Date.now(); // immediately eligible
        try {
          await updateMutation(z);
        } catch (err) {
          console.warn(`[writeQueue.sync] failed to recover zombie ${z.id}:`, (err as Error).message);
        }
      }
      notifyChange();
    } catch (err) {
      console.warn('[writeQueue.sync] zombie recovery scan failed:', (err as Error).message);
    }
  }

  // Tick loop.
  const tickInterval = setInterval(processTick, TICK_MS);

  // Periodic housekeeping — prune synced rows older than 1h.
  const pruneInterval = setInterval(() => {
    pruneSynced(PRUNE_INTERVAL_MS).catch(err =>
      console.warn('[writeQueue.sync] prune failed:', err),
    );
  }, PRUNE_TICK_MS);

  // Kick on network restore / visibility change.
  const onOnline = () => processTick();
  const onVisible = () => {
    if (document.visibilityState === 'visible') processTick();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  // Recover zombies from a prior session, then run the first tick.
  recoverInflightZombies().then(() => processTick());

  activeWorker = {
    stop() {
      if (stopped) return;
      stopped = true;
      clearInterval(tickInterval);
      clearInterval(pruneInterval);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      activeWorker = null;
    },
    kick: processTick,
  };

  return activeWorker;
}

/** Manually nudge the worker (e.g., after JWT refresh). */
export function kick(): void {
  if (activeWorker) activeWorker.kick();
}
